/**
 * The four stacked canvases (docs/03-ARCHITECTURE.md §7.1).
 *
 *   background  grid and paper        redraws rarely
 *   static      finished strokes      redraws when the board changes
 *   active      the stroke in hand    redraws every frame while drawing
 *   overlay     cursors, selection    redraws every frame
 *
 * This file owns the pixels: sizing for the device pixel ratio, clearing, and hand-out
 * of the CanvasLayerHandle a module draws through. What repaints and when belongs to
 * the paint scheduler beside it.
 */

import type { CanvasLayerHandle, CanvasLayerName, ViewportState } from '@opal/contracts';
import type { Rect } from '@opal/shared';

import {
  createPaintScheduler,
  type FrameScheduler,
  type PaintScheduler,
} from './paint-scheduler.js';

export const LAYER_ORDER: readonly CanvasLayerName[] = [
  'background',
  'static',
  'active',
  'overlay',
];

/**
 * The part of a canvas element this file uses.
 *
 * Narrower than HTMLCanvasElement on purpose: it keeps the sizing logic honest about
 * what it touches, and an element satisfies it as it stands.
 */
export interface LayerCanvas {
  width: number;
  height: number;
  readonly style: { width: string; height: string };
  getContext(contextId: '2d'): CanvasRenderingContext2D | null;
}

export type LayerPaint = (ctx: CanvasRenderingContext2D, viewport: ViewportState) => void;

export interface LayerPaintOptions {
  /**
   * Whether the layer is wiped before its painter runs. The active layer sets this
   * false so a long stroke can add only its newest piece each frame instead of
   * redrawing thousands of points — the difference between smooth ink and lag.
   */
  readonly clearFirst?: boolean;
}

export interface CanvasLayers {
  /** Connects a layer to its canvas element, or disconnects it with null. */
  attach(layer: CanvasLayerName, canvas: LayerCanvas | null): void;
  /** Sets what a layer draws. Replacing a painter invalidates that layer. */
  setPaint(layer: CanvasLayerName, paint: LayerPaint, options?: LayerPaintOptions): void;
  /** The handle a module draws through (docs/04-MODULE-CONTRACTS.md §3). */
  handle(layer: CanvasLayerName): CanvasLayerHandle;
  /** Resizes every canvas for a new viewport size or screen. Repaints all four. */
  resize(viewport: ViewportState, dpr: number): void;
  /** New zoom, pan or size. Every layer's content depends on it, so all four repaint. */
  setViewport(viewport: ViewportState): void;
  readonly scheduler: PaintScheduler;
  dispose(): void;
}

/** Clears a layer in device pixels, ignoring the DPR transform. */
function clear(ctx: CanvasRenderingContext2D, canvas: LayerCanvas): void {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
}

interface Layer {
  canvas: LayerCanvas | null;
  ctx: CanvasRenderingContext2D | null;
  paint: LayerPaint | null;
  clearFirst: boolean;
}

/**
 * Sizes one canvas in device pixels and scales its context back to CSS pixels, so
 * everything above draws in CSS pixels and handwriting stays crisp on a retina screen.
 */
function sizeCanvas(
  canvas: LayerCanvas,
  ctx: CanvasRenderingContext2D,
  viewport: ViewportState,
  ratio: number,
): void {
  canvas.width = Math.max(1, Math.round(viewport.widthPx * ratio));
  canvas.height = Math.max(1, Math.round(viewport.heightPx * ratio));
  canvas.style.width = `${viewport.widthPx}px`;
  canvas.style.height = `${viewport.heightPx}px`;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
}

/** The drawing handle a module receives (docs/04-MODULE-CONTRACTS.md §3). */
function makeHandle(
  layer: Layer,
  name: CanvasLayerName,
  dpr: number,
  scheduler: PaintScheduler,
): CanvasLayerHandle {
  const ctx = layer.ctx;
  if (!ctx) throw new Error(`Canvas layer "${name}" has no canvas attached yet`);
  return {
    name,
    ctx,
    dpr,
    invalidate: (_region?: Rect) => {
      // Region-limited repainting arrives with culling at step 2.9; until then a layer
      // repaints whole, which is correct, just not yet clever.
      scheduler.invalidate(name);
    },
    release: () => {
      layer.paint = null;
    },
  };
}

interface LayerStore {
  get(name: CanvasLayerName): Layer;
  each(visit: (layer: Layer, name: CanvasLayerName) => void): void;
}

function createLayerStore(): LayerStore {
  const layers = new Map<CanvasLayerName, Layer>(
    LAYER_ORDER.map((name) => [name, { canvas: null, ctx: null, paint: null, clearFirst: true }]),
  );
  return {
    get(name) {
      const layer = layers.get(name);
      if (!layer) throw new Error(`Unknown canvas layer "${name}"`);
      return layer;
    },
    each(visit) {
      for (const name of LAYER_ORDER) visit(this.get(name), name);
    },
  };
}

/**
 * Teaches the scheduler how to repaint each layer: clear it, then let its own painter
 * draw. A layer only ever repaints itself, which is the whole point of having four.
 */
function bindPainters(
  store: LayerStore,
  scheduler: PaintScheduler,
  readViewport: () => ViewportState,
): void {
  for (const name of LAYER_ORDER) {
    scheduler.setPainter(name, () => {
      const layer = store.get(name);
      if (!layer.ctx || !layer.canvas) return;
      if (layer.clearFirst) clear(layer.ctx, layer.canvas);
      layer.paint?.(layer.ctx, readViewport());
    });
  }
}

export function createCanvasLayers(viewport: ViewportState, frames?: FrameScheduler): CanvasLayers {
  const scheduler = createPaintScheduler(frames);
  const store = createLayerStore();
  let current = viewport;
  let ratio = 1;

  bindPainters(store, scheduler, () => current);
  const invalidateAll = (): void => {
    for (const name of LAYER_ORDER) scheduler.invalidate(name);
  };

  return {
    scheduler,

    attach(name, canvas) {
      const layer = store.get(name);
      layer.canvas = canvas;
      layer.ctx = canvas?.getContext('2d') ?? null;
      if (!canvas || !layer.ctx) return;
      sizeCanvas(canvas, layer.ctx, current, ratio);
      scheduler.invalidate(name);
    },

    setPaint(name, paint, options) {
      const layer = store.get(name);
      layer.paint = paint;
      layer.clearFirst = options?.clearFirst ?? true;
      scheduler.invalidate(name);
    },

    handle: (name) => makeHandle(store.get(name), name, ratio, scheduler),

    resize(next, dpr) {
      ratio = dpr;
      current = next;
      store.each((layer) => {
        if (layer.canvas && layer.ctx) sizeCanvas(layer.canvas, layer.ctx, current, ratio);
      });
      invalidateAll();
    },

    setViewport(next) {
      current = next;
      invalidateAll();
    },

    dispose() {
      scheduler.dispose();
    },
  };
}
